import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { CustomerPayload } from '../../../utils/reducers/customers/customer'
import { TwinDictionary, dictionaryComplexFromJsonArr } from '../../../utils/globals/dictionary'
import { twinFetchPostJSON } from '../../../utils/globals/data'
import { CustomerProjectModelType } from '@teinor/erp/types/company/customer/customerProject'
import { ButtonSecondary } from '../../../baseComponents/Button'
import TwinIcon from '../../../baseComponents/TwinIcon'
import { faPlus } from '@fortawesome/pro-light-svg-icons'
import TwinTrans from '../../../baseComponents/TwinTrans'
import useOnClickOutside from '../../../utils/hooks/useOnClickOut'
import './searcher_customer_project.sass'
import useEditDeleteModal from '../../../utils/hooks/useEditDeleteModal'
import { OnSubmit } from '../../../forms/TwinForm/types'
import ModalCreateCustomerProject from '../ModalCreateCustomerProject'
import ModalCreateCustomerOpportunitysConnect from '../CustomerProfileTabs/CustomerOpportunities/ModalCreateCustomerOpportunity'
import ModalCreateCustomer from '../../../app/erp/screens/Company/CustomerParent/AllCustomers/ModalCreateCustomer'
import { DropDownSearchBarAndCreateSectionProjects } from '../CustomSelectSectionProjects'
import { DropDownSearchBarAndCreateSectionCustomer } from '../CustomSelectSectionCustomers'
import { getConfigParam } from '../../../utils/reducers/getters'
import { CodeCustomerToShowTyInversed } from '@teinor/erp/types/company/config/parameter'

export interface SearcherCustomerProjectProps extends SearcherCustomerProjectLogicProps {
    className?: string
    customers: CustomerPayload
}

const SearcherCustomerProject: React.FC<SearcherCustomerProjectProps> = ({ className, customers, handleAddCustomerProject, preSelectedCustomer, OldProjectId }) => {
    const { showSelectors, handleShowSelector, selectedCustomer, customerProjects, setSelectedCustomer, ref, myProjectOnChange, myProjectOnSubmit, openModal, setOpenModal, myCustomerOnChange, myCustomerOnSubmit } = useSearcherCustomerProjectLogic({ handleAddCustomerProject, preSelectedCustomer, OldProjectId })
    let idOrCode = ''
    const fieldMemo = useMemo(() => {
        const codeCustomerToShow = getConfigParam('codeCustomerToShow')
        let fieldId: 'id' | 'code' = 'id'
        if (parseInt(String(codeCustomerToShow)) === parseInt(String(CodeCustomerToShowTyInversed['codeCustomer']))) {
            fieldId = 'code'
        }
        return fieldId
    }, [])
    if (selectedCustomer && customers) {
        idOrCode = customers?.all?.[selectedCustomer]?.[fieldMemo] ? (customers?.all?.[selectedCustomer]?.[fieldMemo] + ' - ') : ''
    }
    return (
        <div className='flex items-center w-full mr-50 ' >
            <ButtonSecondary onClick={handleShowSelector}>
                <TwinIcon icon={faPlus} className='mr-5' />
                <TwinTrans transKey='add'>Añadir</TwinTrans>
            </ButtonSecondary>
            {showSelectors ?
                <div ref={ref} className='w-full'>
                    <div className={'searcher_customer_project w-full ' + (className || '')} >
                        {!selectedCustomer ?
                            <DropDownSearchBarAndCreateSectionCustomer customers={customers} onChange={myCustomerOnChange} value={''} />
                            :
                            <div>
                                <div className='flex relative searcher_project_container'>
                                    <div className='h-34 bg-gray-F7 px-12 flex items-center cursor-pointer w-fit max-w-1/2 twin_elipsis ' onClick={() => setSelectedCustomer(null)}>{idOrCode + customers?.all?.[selectedCustomer]?.name}</div>
                                    <div className='relative mr-20'>
                                        <div className='arrow_right'></div>
                                    </div>
                                    {Object.keys(customerProjects).length ?
                                        <DropDownSearchBarAndCreateSectionProjects onChange={myProjectOnChange} projects={customerProjects} />
                                        : <div className='flex items-center'><TwinTrans transKey='noProjects'>No tiene proyectos</TwinTrans></div>
                                    }
                                </div>
                            </div>
                        }
                        </div>
                        {openModal?.type === 'create' && <ModalCreateCustomerProject setOpened={() => setOpenModal(null)} onSubmit={myProjectOnSubmit} basicData={{ CustomerId: selectedCustomer }} />}
                        {openModal?.type === 'edit' && <ModalCreateCustomerOpportunitysConnect setOpened={() => setOpenModal(null)} onSubmit={myProjectOnSubmit} basicData={{ CustomerId: selectedCustomer }} />}
                        {openModal?.type === 'delete' && <ModalCreateCustomer setOpened={() => setOpenModal(null)} onSubmit={myCustomerOnSubmit} />}
                    </div>
                : null
            }
        </div>
    )
}

interface SearcherCustomerProjectLogicProps {
    handleAddCustomerProject: (CustomerProject: CustomerProjectModelType) => Promise<boolean>
    preSelectedCustomer?: number
    OldProjectId?: number
}

const useSearcherCustomerProjectLogic = ({ handleAddCustomerProject, preSelectedCustomer, OldProjectId }: SearcherCustomerProjectLogicProps) => {
    const [showSelectors, setShowSelectors] = useState<boolean>(preSelectedCustomer? true : false)
    const [selectedCustomer, setSelectedCustomer] = useState<number | null>(preSelectedCustomer || null)
    const [customerProjects, setCustomerProjects] = useState<TwinDictionary>({})
    const { openModal, setOpenModal } = useEditDeleteModal()
    const ref = useRef<any>(null)

    useOnClickOutside(ref, () => handleShowSelector())
    
    const handleSelectCustomerProject = useCallback(async (CustomerProject: CustomerProjectModelType) => {
        setShowSelectors(false)
        setSelectedCustomer(null)
        setCustomerProjects({})
        await handleAddCustomerProject(CustomerProject)
    }, [setCustomerProjects, handleAddCustomerProject])

    const myProjectOnChange = useCallback((value: number) => {
        if (value === 0) {
            setOpenModal({ type: 'create', allRowData: {} })
        } else if (value === -1) {
            setOpenModal({ type: 'edit', allRowData: {} })
        } else if (value) {
            handleSelectCustomerProject(customerProjects[value])
        }
    }, [setOpenModal, handleSelectCustomerProject, customerProjects])

    const handleShowSelector = useCallback(() => {
        if(!preSelectedCustomer){
            if (showSelectors){
                setSelectedCustomer(null)
                setCustomerProjects({})
            }
            setShowSelectors((old)=>!old)
        }else if(OldProjectId){
            handleSelectCustomerProject(customerProjects[OldProjectId])
        }
    }, [setShowSelectors, showSelectors, preSelectedCustomer, handleSelectCustomerProject, OldProjectId, customerProjects])

    const getCustomerProjects = useCallback(async(CustomerId?: number) => {
        if(CustomerId){
            const result = await twinFetchPostJSON('/api/app/customer/customerProject/getAllCustomerProjects', {
                where: {
                    CustomerId,
                    'CustomerProject.active': true
                }
            })
            if (result) {
                const projectsDict = dictionaryComplexFromJsonArr(result)
                setCustomerProjects(projectsDict)
                return projectsDict
            }
        }
    }, [setCustomerProjects])

    const myProjectOnSubmit: OnSubmit = useCallback(async (e) => {
        const result = await e.json()
        if (result) {
            if (selectedCustomer) {
                const projectsDict = await getCustomerProjects(selectedCustomer)
                if (projectsDict) {
                    handleSelectCustomerProject(projectsDict[result?.id])
                    setOpenModal(null)
                }
            }
        }
    }, [setOpenModal, handleSelectCustomerProject, selectedCustomer, getCustomerProjects])

    const handleSelectedCustomer = useCallback(async(id: number) => {
        setSelectedCustomer(id)
        await getCustomerProjects(id)
    }, [setSelectedCustomer, getCustomerProjects])

    const myCustomerOnChange = useCallback((value: number) => {
        let myValue: number | '' = value
        if (value === 0) {
            myValue = ''
            setOpenModal({ type: 'delete', allRowData: {} })
        }
        if (myValue) {
            handleSelectedCustomer(myValue)
        }
    }, [setOpenModal, handleSelectedCustomer])

    const myCustomerOnSubmit: OnSubmit = useCallback(async (e) => {
        const result = await e.json()
        if (result) {
            handleSelectedCustomer(result?.id)
            setOpenModal(null)
        }
    }, [setOpenModal, handleSelectedCustomer])

    useEffect(() => {
        if (showSelectors) {
            if (ref && ref.current) {
                const inputParentRef = ref?.current?.querySelector('.input_with_icon')
                if (inputParentRef) {
                    inputParentRef.children[0].focus()
                }
            }
        }
    }, [showSelectors, ref])

    useEffect(() => {
        getCustomerProjects(preSelectedCustomer)
    }, [getCustomerProjects, preSelectedCustomer])
    
    return { handleShowSelector, showSelectors, selectedCustomer, customerProjects, setSelectedCustomer, ref, myProjectOnChange, myProjectOnSubmit, openModal, setOpenModal, myCustomerOnChange, myCustomerOnSubmit }
}


export default SearcherCustomerProject